import * as React from 'react';
import { Palette } from 'lucide-react';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '@/lib/utils';

interface ColorPickerProps {
  value?: string;
  onChange?: (color: string) => void;
  disabled?: boolean;
  className?: string;
}

const predefinedColors = [
  '#ef4444', // red-500
  '#f97316', // orange-500
  '#eab308', // yellow-500
  '#22c55e', // green-500
  '#06b6d4', // cyan-500
  '#3b82f6', // blue-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#6b7280', // gray-500
  '#2F2E37', // dark-900
  '#000000', // black
  '#fbbf24', // amber-400
  '#34d399', // emerald-400
  '#60a5fa', // blue-400
  '#a78bfa', // violet-400
  '#f472b6', // pink-400
];

export function ColorPicker({
  value = '#3b82f6',
  onChange,
  disabled = false,
  className,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [customColor, setCustomColor] = React.useState(value);

  const handleColorSelect = (color: string) => {
    onChange?.(color);
    setIsOpen(false);
  };

  const handleCustomColorChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newColor = event.target.value;
    setCustomColor(newColor);
    onChange?.(newColor);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <div className='flex items-center gap-2'>
            <div
              className='h-4 w-4 rounded border border-border'
              style={{ backgroundColor: value }}
            />
            <Palette className='h-4 w-4' />
            <span>{value}</span>
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-72 p-4' align='start'>
        <div className='space-y-4'>
          <div>
            <h4 className='text-sm font-medium leading-none mb-3'>Colors</h4>
            <div className='grid grid-cols-8 gap-1.5'>
              {predefinedColors.map((color) => (
                <button
                  key={color}
                  type='button'
                  onClick={() => handleColorSelect(color)}
                  className={cn(
                    'h-7 w-7 rounded border border-border hover:scale-105 transition-transform',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                    value === color && 'ring-2 ring-ring ring-offset-1',
                  )}
                  style={{ backgroundColor: color }}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </div>
          </div>

          <div className='space-y-3'>
            <h4 className='text-sm font-medium leading-none'>Custom</h4>
            <div className='flex items-center gap-3'>
              <input
                type='color'
                value={customColor}
                onChange={handleCustomColorChange}
                className={cn(
                  'h-9 w-12 rounded border border-border cursor-pointer p-0',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                )}
              />
              <input
                type='text'
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                onBlur={() => onChange?.(customColor)}
                placeholder='#ffffff'
                className={cn(
                  'flex-1 h-9 px-3 text-sm border border-border rounded',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                  'bg-background',
                )}
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
